import { useState, useMemo, useEffect, useRef } from "react";
import { X, Zap, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";
import { useEvents } from "@/hooks/useConflictData";
import { motion, AnimatePresence } from "framer-motion";
import PricingDialog from "@/components/PricingDialog";
import { trackConversion } from "@/lib/trackConversion";
import { useGuestCheckout } from "@/hooks/useGuestCheckout";
import { useIsMobile } from "@/hooks/use-mobile";

const DISMISS_KEY = "premium_bar_dismissed";

const StickyUpgradeBar = () => {
  const { isPremium } = useAuth();
  const { formatPriceWithEur } = useLocalCurrency();
  const { data: events } = useEvents(undefined, false);
  const { startCheckout, loading: checkoutLoading } = useGuestCheckout();
  const isMobile = useIsMobile();
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem(DISMISS_KEY) === "1"
  );
  const [pricingOpen, setPricingOpen] = useState(false);
  const [missedCount, setMissedCount] = useState(0);
  const [browseMinutes, setBrowseMinutes] = useState(0);
  const startTime = useRef(Date.now());

  // Track missed events since page load
  useEffect(() => {
    if (isPremium || dismissed) return;
    const interval = setInterval(() => {
      const mins = Math.floor((Date.now() - startTime.current) / 60000);
      setBrowseMinutes(mins);
    }, 10000);
    return () => clearInterval(interval);
  }, [isPremium, dismissed]);

  // Count new events since page load
  useEffect(() => {
    if (!events?.length || isPremium) return;
    const loadTime = startTime.current;
    const newEvents = events.filter(
      (e) => new Date(e.occurred_at).getTime() > loadTime
    ).length;
    // Simulate missed events growing over time for demo
    const elapsed = Math.floor((Date.now() - loadTime) / 30000); // every 30s
    setMissedCount(Math.max(newEvents, elapsed));
  }, [events, isPremium]);

  // Hide on mobile to save screen space
  if (isPremium || dismissed || isMobile) return null;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  return (
    <AnimatePresence>
      <motion.div
        key="sticky-upgrade-bar"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-primary/30 bg-card/95 backdrop-blur-md"
      >
        <div className="container mx-auto flex items-center justify-between gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-2.5">
          <div className="hidden items-center gap-3 sm:flex">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/20">
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {missedCount > 0 ? (
                  <span className="flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                    You've missed {missedCount} event{missedCount !== 1 ? "s" : ""}{browseMinutes > 0 ? ` in the last ${browseMinutes} min` : " while browsing"}
                  </span>
                ) : (
                  "Unlock real-time conflict intelligence"
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                Real-time push alerts · 56 OSINT tools · AI analysis
              </p>
            </div>
          </div>
          {/* Mobile */}
          <div className="flex items-center gap-2 sm:hidden">
            <Zap className="h-4 w-4 shrink-0 text-primary" />
            <p className="text-xs font-semibold text-foreground">
              {missedCount > 0 ? (
                <span>🔒 {missedCount} events you're missing</span>
              ) : (
                "Unlock full access"
              )}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <Button
              size="sm"
              className="gap-1 text-xs font-semibold sm:gap-1.5 sm:text-sm"
              onClick={() => { trackConversion("sticky_bar_clicked"); startCheckout("monthly"); }}
              disabled={checkoutLoading}
            >
              <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              Get Full Access — {formatPriceWithEur(2.99)}/mo
            </Button>
            <button
              onClick={() => { trackConversion("sticky_bar_see_plans"); setPricingOpen(true); }}
              className="hidden sm:inline text-[11px] text-primary hover:underline"
            >
              All plans
            </button>
            <button
              onClick={handleDismiss}
              className="rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </motion.div>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </AnimatePresence>
  );
};

export default StickyUpgradeBar;
